import React, { useCallback, useState, useEffect } from 'react'
import { TransactionResponse } from '@ethersproject/providers'
import { Currency, TokenAmount, ETHER, Token } from '@uniswap/sdk-core'
import { WETH9 } from '@uniswap/sdk-core'
import { useV3NFTPositionManagerContract, useV3SingleSideRouterContract } from '../../hooks/useContract'
import { RouteComponentProps } from 'react-router-dom'
import { Text } from 'rebass'

import { ButtonPrimary } from '../../components/Button'
import { AutoColumn } from '../../components/Column'
import TransactionConfirmationModal, { ConfirmationModalContent } from '../../components/TransactionConfirmationModal'

import { useIsSwapUnsupported } from '../../hooks/useIsSwapUnsupported'
import { useUSDCValue } from '../../hooks/useUSDCPrice'
import Review from './Review'
import { useActiveWeb3React } from '../../hooks'
import { useCurrency } from '../../hooks/Tokens'
import { ApprovalState, useApproveCallback } from '../../hooks/useApproveCallback'
import useTransactionDeadline from '../../hooks/useTransactionDeadline'
import { Field, Bound } from '../../state/mint/v3/actions'

import { useTransactionAdder } from '../../state/transactions/hooks'
import { useIsExpertMode, useUserSlippageTolerance } from '../../state/user/hooks'
import { maxAmountSpend } from '../../utils/maxAmountSpend'
import { currencyId } from '../../utils/currencyId'
import UnsupportedCurrencyFooter from 'components/swap/UnsupportedCurrencyFooter'
import { Wrapper, ScrollablePage, RowContainer, CustomAppBody } from './styled'
import { useV3MintState, useV3MintActionHandlers, useV3DerivedMintInfo } from 'state/mint/v3/hooks'
import { FeeAmount, NonfungiblePositionManager } from '@uniswap/v3-sdk'
import { NONFUNGIBLE_POSITION_MANAGER_ADDRESSES } from 'constants/v3'
import { useV3PositionFromTokenId } from 'hooks/useV3Positions'
import { useDerivedPositionInfo } from 'hooks/useDerivedPositionInfo'

import { BigNumber } from '@ethersproject/bignumber'
import { calculateGasMargin } from 'utils'
import { Helmet } from 'react-helmet'
import { CURRENT_CHAIN, V3_SINGLE_SIDE_ADDRESS } from '@hyperswap/addresses'

import { AddLiquidityAddRemoveTabs } from 'components/NavigationTabs/AddLiquidityNavigationTabs'
import TagButton from 'components/TagButton'
import Steps from './Stepper/Steps/index'
import Step0 from './Stepper/Step0'
import Step1 from './Stepper/Step1'
import Step2 from './Stepper/Step2'
import Step3 from './Stepper/Step3'
import { useV3PairExists } from 'hooks/useV3PairExists'
import { usePrivy } from '@privy-io/react-auth'
export type PresetPriceRange = 'maxRange' | 'passive' | 'wide' | 'narrow' | 'aggressive' | null

export default function AddLiquidity({
  match: {
    params: { currencyIdA, currencyIdB, feeAmount: feeAmountFromUrl, tokenId },
  },
  history,
}: RouteComponentProps<{ currencyIdA?: string; currencyIdB?: string; feeAmount?: string; tokenId?: string }>) {
  const [activeStep, setActiveStep] = useState(0)
  const [initAddExistingPosition, setInitAddExistingPosition] = useState(false)
  const [successCreate, setSuccessCreate] = useState(false)
  const [singleSide, setSingleSide] = useState(false)

  const { account, chainId, library } = useActiveWeb3React()
  const { login } = usePrivy()
  const expertMode = useIsExpertMode()
  const addTransaction = useTransactionAdder()
  const positionManager = useV3NFTPositionManagerContract()

  // check for existing position if tokenId in url
  const { position: existingPositionDetails, loading: positionLoading } = useV3PositionFromTokenId(
    tokenId ? BigNumber.from(tokenId) : undefined
  )
  const hasExistingPosition = !!existingPositionDetails && !positionLoading
  const { position: existingPosition } = useDerivedPositionInfo(existingPositionDetails)

  // fee selection from url
  const feeAmount: FeeAmount | undefined =
    feeAmountFromUrl && Object.values(FeeAmount).includes(parseFloat(feeAmountFromUrl))
      ? parseFloat(feeAmountFromUrl)
      : undefined

  const currencyA = useCurrency(currencyIdA)
  const currencyB = useCurrency(currencyIdB)

  const [baseCurrency, setBaseCurrency] = useState(currencyA)

  const { exists } = useV3PairExists(
    currencyA instanceof Token ? currencyA : WETH9[CURRENT_CHAIN],
    currencyB instanceof Token ? currencyB : WETH9[CURRENT_CHAIN],
    feeAmount
  )

  const [presetPriceRange, setPresetPriceRange] = React.useState<PresetPriceRange>(null)

  // keep track for UI display purposes of user selected base currency

  useEffect(() => {
    if (hasExistingPosition && existingPosition && initAddExistingPosition === false) {
      handleStep(2)
      setInitAddExistingPosition(true)
    }
    // eslint-disable-next-line
  }, [hasExistingPosition, existingPosition, initAddExistingPosition])

  // mint state
  const { independentField, typedValue, startPriceTypedValue } = useV3MintState()

  const {
    pool,
    ticks,
    dependentField,
    price,
    pricesAtTicks,
    parsedAmounts,
    currencyBalances,
    position,
    noLiquidity,
    currencies,
    errorMessage,
    invalidPool,
    invalidRange,
    outOfRange,
    depositADisabled,
    depositBDisabled,
    invertPrice,
  } = useV3DerivedMintInfo(
    currencyA ?? undefined,
    currencyB ?? undefined,
    feeAmount,
    baseCurrency ?? undefined,
    existingPosition
  )

  const { onFieldAInput, onFieldBInput, onLeftRangeInput, onRightRangeInput, onStartPriceInput } =
    useV3MintActionHandlers(noLiquidity)

  const routerForSingleSide = useV3SingleSideRouterContract()

  // modal and loading
  const [showConfirm, setShowConfirm] = useState<boolean>(false)
  const [attemptingTxn, setAttemptingTxn] = useState<boolean>(false) // clicked confirm
  const [errorSingleSide, setErrorSingleSide] = useState<string>('')
  // txn values
  const deadline = useTransactionDeadline() // custom from users settings
  const [allowedSlippage] = useUserSlippageTolerance() // custom from users
  const [txHash, setTxHash] = useState<string>('')

  const [triggerInfiniteRange, setTriggerInfiniteRange] = useState<boolean>(false)

  // get formatted amounts
  const formattedAmounts = {
    [independentField]: typedValue,
    [dependentField]: parsedAmounts[dependentField]?.toSignificant(6) ?? '',
  }

  const usdcValues = {
    [Field.CURRENCY_A]: useUSDCValue(parsedAmounts[Field.CURRENCY_A]),
    [Field.CURRENCY_B]: useUSDCValue(parsedAmounts[Field.CURRENCY_B]),
  }
  // get the max amounts user can add
  const maxAmounts: { [field in Field]?: TokenAmount } = [Field.CURRENCY_A, Field.CURRENCY_B].reduce(
    (accumulator, field) => {
      return {
        ...accumulator,
        [field]: maxAmountSpend(currencyBalances[field]),
      }
    },
    {}
  )

  const atMaxAmounts: { [field in Field]?: TokenAmount } = [Field.CURRENCY_A, Field.CURRENCY_B].reduce(
    (accumulator, field) => {
      return {
        ...accumulator,
        [field]: maxAmounts[field]?.equalTo(parsedAmounts[field] ?? '0'),
      }
    },
    {}
  )

  // check whether the user has approved the router on the tokens
  const [approvalA, approveACallback] = useApproveCallback(
    parsedAmounts[Field.CURRENCY_A],
    chainId
      ? NONFUNGIBLE_POSITION_MANAGER_ADDRESSES[chainId as keyof typeof NONFUNGIBLE_POSITION_MANAGER_ADDRESSES]
      : undefined
  )
  const [approvalB, approveBCallback] = useApproveCallback(
    parsedAmounts[Field.CURRENCY_B],
    chainId
      ? NONFUNGIBLE_POSITION_MANAGER_ADDRESSES[chainId as keyof typeof NONFUNGIBLE_POSITION_MANAGER_ADDRESSES]
      : undefined
  )

  const [approvalAForSingleSide, approveACallbackForSingleSide] = useApproveCallback(
    parsedAmounts[Field.CURRENCY_A],
    V3_SINGLE_SIDE_ADDRESS
  )

  const handleCurrencySelect = useCallback(
    (currencyNew: Currency, currencyIdOther?: string): (string | undefined)[] => {
      const currencyIdNew = currencyId(currencyNew)

      if (currencyIdNew === currencyIdOther) {
        // not ideal, but for now clobber the other if the currency ids are equal
        return [currencyIdNew, undefined]
      } else {
        // prevent weth + eth
        const isETHOrWETHNew =
          currencyIdNew === 'HYPE' ||
          (chainId !== undefined && currencyIdNew === WETH9[chainId as keyof typeof WETH9]?.address)
        const isETHOrWETHOther =
          currencyIdOther !== undefined &&
          (currencyIdOther === 'HYPE' ||
            (chainId !== undefined && currencyIdOther === WETH9[chainId as keyof typeof WETH9]?.address))

        if (isETHOrWETHNew && isETHOrWETHOther) {
          return [currencyIdNew, undefined]
        } else {
          return [currencyIdNew, currencyIdOther]
        }
      }
    },
    [chainId]
  )

  const handleCurrencyASelect = useCallback(
    (currencyANew: Currency) => {
      const [idA, idB] = handleCurrencySelect(currencyANew, currencyIdB)
      if (idB === undefined) {
        history.push(`/add/${idA}`)
      } else {
        history.push(`/add/${idA}/${idB}`)
      }

      onLeftRangeInput('')
      onRightRangeInput('')
    },
    [handleCurrencySelect, currencyIdB, onLeftRangeInput, onRightRangeInput, history]
  )

  const handleCurrencyBSelect = useCallback(
    (currencyBNew: Currency) => {
      const [idB, idA] = handleCurrencySelect(currencyBNew, currencyIdA)
      if (idA === undefined) {
        history.push(`/add/${idB}`)
      } else {
        history.push(`/add/${idA}/${idB}`)
      }
      onLeftRangeInput('')
      onRightRangeInput('')
    },
    [handleCurrencySelect, currencyIdA, onLeftRangeInput, onRightRangeInput, history]
  )

  const handleFeePoolSelect = useCallback(
    (newFeeAmount: FeeAmount) => {
      history.push(`/add/${currencyIdA}/${currencyIdB}/${newFeeAmount}`)
    },
    [currencyIdA, currencyIdB, history]
  )

  const handleDismissConfirmation = useCallback(() => {
    setShowConfirm(false)
    // if there was a tx hash, we want to clear the input
    if (txHash) {
      onFieldAInput('')
      history.push('/pool')
    }
    setTxHash('')
  }, [history, onFieldAInput, txHash])

  const addIsUnsupported = useIsSwapUnsupported(currencies?.CURRENCY_A, currencies?.CURRENCY_B)

  const clearAll = useCallback(() => {
    onFieldAInput('')
    onFieldBInput('')
    onLeftRangeInput('')
    onRightRangeInput('')
    history.push(`/add/HYPE`)
    history.go(0)
  }, [history, onFieldAInput, onFieldBInput, onLeftRangeInput, onRightRangeInput])

  // get value and prices at ticks
  const { [Bound.LOWER]: tickLower, [Bound.UPPER]: tickUpper } = ticks
  const { [Bound.LOWER]: priceLower, [Bound.UPPER]: priceUpper } = pricesAtTicks

  async function onAdd() {
    if (!chainId || !library || !account) return

    if (!positionManager || !currencyA || !currencyB) {
      return
    }

    if (position && account && deadline) {
      const { calldata, value } =
        hasExistingPosition && tokenId
          ? NonfungiblePositionManager.addCallParameters(position, {
              tokenId,
              slippageTolerance: allowedSlippage,
              deadline: deadline.toString(),
              useEther: currencyA === ETHER || currencyB === ETHER,
            })
          : NonfungiblePositionManager.addCallParameters(position, {
              slippageTolerance: allowedSlippage,
              recipient: account,
              deadline: deadline.toString(),
              useEther: currencyA === ETHER || currencyB === ETHER,
              createPool: noLiquidity,
            })

      const txn = {
        to: NONFUNGIBLE_POSITION_MANAGER_ADDRESSES[chainId as keyof typeof NONFUNGIBLE_POSITION_MANAGER_ADDRESSES],
        data: calldata,
        value,
      }

      setAttemptingTxn(true)

      library
        .getSigner()
        .estimateGas(txn)
        .then((estimate: any) => {
          console.log('AddLiquidity V3 gasLimit', calculateGasMargin(estimate).toString())
          const newTxn = {
            ...txn,
            gasLimit: calculateGasMargin(estimate),
          }

          return library
            .getSigner()
            .sendTransaction(newTxn)
            .then((response: TransactionResponse) => {
              setAttemptingTxn(false)
              addTransaction(response, {
                summary: noLiquidity
                  ? `Create pool and add ${currencyA?.symbol}/${currencyB?.symbol} V3 liquidity`
                  : `Add ${currencyA?.symbol}/${currencyB?.symbol} V3 liquidity`,
              })
              setTxHash(response.hash)
            })
        })
        .catch((error: any) => {
          setAttemptingTxn(false)
          // we only care if the error is something _other_ than the user rejected the tx
          if (error?.code !== 4001) {
            console.error(error)
          }
        })
    } else {
      return
    }
  }

  async function onAddSingleSide() {
    if (!chainId || !library || !account || !routerForSingleSide) return

    const { [Field.CURRENCY_A]: parsedAmountA } = parsedAmounts

    if (!parsedAmountA || !currencyA || !deadline) {
      return
    }

    const estimate = routerForSingleSide.estimateGas.provideLiquidityV3
    const method = routerForSingleSide.provideLiquidityV3
    const tokenAisETH = currencyA === ETHER
    setErrorSingleSide('')
    const tokenAAddress = currencyA instanceof Token ? currencyA?.address : WETH9[CURRENT_CHAIN].address
    const tokenBAddress = currencyB instanceof Token ? currencyB?.address : WETH9[CURRENT_CHAIN].address
    const amountA = parsedAmountA?.raw.toString()
    const slippage = allowedSlippage.numerator.toString()

    const value = tokenAisETH ? BigNumber.from(parsedAmountA.raw.toString()) : null

    console.log('args', [tokenAAddress, tokenBAddress, amountA, feeAmount, slippage])
    console.log('value', value)
    setAttemptingTxn(true)
    await estimate([tokenAAddress, tokenBAddress, amountA, feeAmount, slippage], value ? { value } : {})
      .then((estimatedGasLimit: any) => {
        console.log('AddLiquidityV2 estimatedGasLimit', estimatedGasLimit.toString())
        return method([tokenAAddress, tokenBAddress, amountA, feeAmount, slippage], {
          ...(value ? { value } : {}),
          gasLimit: calculateGasMargin(estimatedGasLimit),
        })
          .then((response: any) => {
            setAttemptingTxn(false)

            addTransaction(response, {
              summary:
                'Add ' + parsedAmounts[Field.CURRENCY_A]?.toSignificant(3) + ' ' + currencies[Field.CURRENCY_A]?.symbol,
            })

            setTxHash(response.hash)
          })
          .catch((error: any) => {
            setAttemptingTxn(false)
            console.error(error)
          })
      })
      .catch((error: any) => {
        setAttemptingTxn(false)
        setShowConfirm(false)

        if (error?.data?.message === 'execution reverted: revert: Price slippage check') {
          setErrorSingleSide('Try increasing the slippage tolerance.')
        } else if (error?.data?.message === 'execution reverted: revert: HyperswapRouter: INSUFFICIENT_B_AMOUNT') {
          setErrorSingleSide('Not enough liquidity in the pool.')
        } else {
          setErrorSingleSide('An error has occurred. Try increasing the slippage tolerance.')
        }

        console.log(error?.data?.message)
        console.error(error)
      })
  }

  React.useEffect(() => {
    if (singleSide === true) {
      onFieldBInput('')
    }
  }, [singleSide, onFieldBInput])

  React.useEffect(() => {
    if (presetPriceRange !== 'maxRange') {
      setSingleSide(false)
    }
  }, [presetPriceRange, setSingleSide])

  const pendingText = `Supplying ${!depositADisabled ? parsedAmounts[Field.CURRENCY_A]?.toSignificant(6) : ''} ${
    !depositADisabled ? currencies[Field.CURRENCY_A]?.symbol : ''
  } ${!outOfRange && !singleSide ? 'and' : ''} ${
    !depositBDisabled && !singleSide ? parsedAmounts[Field.CURRENCY_B]?.toSignificant(6) : ''
  } ${!depositBDisabled && !singleSide ? currencies[Field.CURRENCY_B]?.symbol : ''}`

  // we need an existence check on parsed amounts for single-asset deposits
  const showApprovalA = approvalA !== ApprovalState.APPROVED && !!parsedAmounts[Field.CURRENCY_A]
  const showApprovalB = approvalB !== ApprovalState.APPROVED && !!parsedAmounts[Field.CURRENCY_B]
  const showApprovalAForSingleSide =
    approvalAForSingleSide !== ApprovalState.APPROVED && !!parsedAmounts[Field.CURRENCY_A]
  const pairDoesNotExists = !exists

  const step0Validate = !!(currencies.CURRENCY_A && currencies.CURRENCY_B && feeAmount)
  const step1Validate = !(tickLower === undefined || tickUpper === undefined || invalidPool || invalidRange)
  const step2Validate = !pairDoesNotExists || successCreate

  const handleStep = useCallback(
    (newStep: number, force?: boolean) => {
      if (activeStep === 1 && newStep === 2 && step2Validate) {
        setActiveStep(3)
      } else if (
        newStep < activeStep ||
        (newStep === 1 && step0Validate) ||
        (newStep === 2 && step1Validate) ||
        (newStep === 3 && step2Validate)
      ) {
        setActiveStep(newStep)
      } else if (force === true) {
        setActiveStep(newStep)
      }
    },
    [activeStep, step0Validate, step1Validate, step2Validate]
  )

  useEffect(() => {
    if (step2Validate && activeStep === 2) {
      setActiveStep(3)
    }
  }, [step2Validate, activeStep])

  const isValid = !errorMessage && !invalidRange
  const switchTokenA = () => {
    history.push(`/add/${currencyIdB as string}/${currencyIdA as string}${feeAmount ? '/' + feeAmount : ''}`)
  }
  return (
    <>
      <Helmet>
        <title>Add Liquidity to V3 Pools | HyperSwap Exchange on Hyperliquid</title>
        <meta
          name="description"
          content="Provide concentrated liquidity to V3 pools on HyperSwap Exchange. Built on Hyperliquid, optimize your earnings with advanced DeFi strategies."
        />
        <meta
          name="keywords"
          content="HyperSwap V3 liquidity, concentrated liquidity, add liquidity, Hyperliquid blockchain, DeFi pools, Hyperliquid EVM"
        />
        <meta property="og:title" content="Add Liquidity to V3 Pools | HyperSwap Exchange on Hyperliquid" />
        <meta
          property="og:description"
          content="Add liquidity to V3 pools on HyperSwap Exchange, leveraging Hyperliquid's innovative blockchain technology."
        />
        <meta property="og:url" content={`${window.location.origin}/#/add/HYPE`} />
      </Helmet>
      <div>
        <AddLiquidityAddRemoveTabs />
        <ScrollablePage>
          <TransactionConfirmationModal
            isOpen={showConfirm}
            onDismiss={handleDismissConfirmation}
            attemptingTxn={attemptingTxn}
            hash={txHash}
            content={() => (
              <ConfirmationModalContent
                title={'Add Liquidity'}
                onDismiss={handleDismissConfirmation}
                topContent={() => (
                  <Review
                    parsedAmounts={parsedAmounts}
                    position={position}
                    existingPosition={existingPosition}
                    priceLower={priceLower}
                    priceUpper={priceUpper}
                    outOfRange={outOfRange}
                    singleSide={singleSide}
                  />
                )}
                bottomContent={() => (
                  <ButtonPrimary style={{ marginTop: '1rem' }} onClick={singleSide ? onAddSingleSide : onAdd}>
                    <Text fontWeight={500} fontSize={20}>
                      Add
                    </Text>
                  </ButtonPrimary>
                )}
              />
            )}
            pendingText={pendingText}
          />
          <AutoColumn>
            <RowContainer>
              <Steps
                activeStep={activeStep}
                step0Validate={step0Validate}
                step1Validate={step1Validate}
                handleStep={handleStep}
              />

              <CustomAppBody
                width={[0, 3].includes(activeStep) ? 'fit-content' : '100%'}
                height={activeStep === 0 ? 'fit-content' : ''}
              >
                <Wrapper>
                  {currencyA && currencyB && (
                    <div style={{ maxWidth: '68px', marginLeft: 'auto' }}>
                      <TagButton onClick={() => clearAll()}>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="icon icon-tabler icons-tabler-outline icon-tabler-restore"
                        >
                          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                          <path d="M3.06 13a9 9 0 1 0 .49 -4.087" />
                          <path d="M3 4.001v5h5" />
                          <path d="M12 12m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" />
                        </svg>
                        Reset
                      </TagButton>
                    </div>
                  )}
                  <AutoColumn gap="32px">
                    <Step0
                      activeStep={activeStep}
                      hasExistingPosition={hasExistingPosition}
                      existingPosition={existingPosition}
                      outOfRange={outOfRange}
                      currencyA={currencyA}
                      currencyB={currencyB}
                      currencies={currencies}
                      formattedAmounts={formattedAmounts}
                      maxAmounts={maxAmounts}
                      atMaxAmounts={atMaxAmounts}
                      onFieldAInput={onFieldAInput}
                      onFieldBInput={onFieldBInput}
                      handleCurrencyASelect={handleCurrencyASelect}
                      handleCurrencyBSelect={handleCurrencyBSelect}
                      feeAmount={feeAmount}
                      handleFeePoolSelect={handleFeePoolSelect}
                      step0Validate={step0Validate}
                      handleStep={handleStep}
                    />
                    <Step1
                      activeStep={activeStep}
                      noLiquidity={noLiquidity}
                      currencyA={currencyA}
                      currencyB={currencyB}
                      feeAmount={feeAmount}
                      currencyIdA={currencyIdA}
                      currencyIdB={currencyIdB}
                      startPriceTypedValue={startPriceTypedValue}
                      onStartPriceInput={onStartPriceInput}
                      price={price}
                      invertPrice={invertPrice}
                      onLeftRangeInput={onLeftRangeInput}
                      onRightRangeInput={onRightRangeInput}
                      history={history}
                      hasExistingPosition={hasExistingPosition}
                      existingPosition={existingPosition}
                      outOfRange={outOfRange}
                      invalidPool={invalidPool}
                      invalidRange={invalidRange}
                      step1Validate={step1Validate}
                      handleStep={handleStep}
                      priceLower={priceLower}
                      priceUpper={priceUpper}
                      setPresetPriceRange={setPresetPriceRange}
                      presetPriceRange={presetPriceRange}
                      tickLower={tickLower}
                      tickUpper={tickUpper}
                      pool={pool}
                      baseCurrency={baseCurrency}
                      setBaseCurrency={setBaseCurrency}
                      triggerInfiniteRange={triggerInfiniteRange}
                      setTriggerInfiniteRange={setTriggerInfiniteRange}
                    />

                    <Step2
                      activeStep={activeStep}
                      pairDoesNotExists={pairDoesNotExists}
                      step2Validate={step2Validate}
                      handleStep={handleStep}
                      setSuccessCreate={setSuccessCreate}
                      successCreate={successCreate}
                      currencyA={currencyA}
                      currencyB={currencyB}
                      feeAmount={feeAmount}
                    />

                    <Step3
                      activeStep={activeStep}
                      tickLower={tickLower}
                      tickUpper={tickUpper}
                      invalidPool={invalidPool}
                      invalidRange={invalidRange}
                      hasExistingPosition={hasExistingPosition}
                      formattedAmounts={formattedAmounts}
                      maxAmounts={maxAmounts}
                      atMaxAmounts={atMaxAmounts}
                      currencies={currencies}
                      usdcValues={usdcValues}
                      onFieldAInput={onFieldAInput}
                      onFieldBInput={onFieldBInput}
                      depositADisabled={depositADisabled}
                      depositBDisabled={depositBDisabled}
                      addIsUnsupported={addIsUnsupported}
                      account={account}
                      toggleWalletModal={() =>
                        login({
                          loginMethods: ['wallet'],
                          walletChainType: 'ethereum-only',
                          disableSignup: false,
                        })
                      }
                      approvalA={approvalA}
                      approvalB={approvalB}
                      approvalAForSingleSide={approvalAForSingleSide}
                      approveACallback={approveACallback}
                      approveBCallback={approveBCallback}
                      approveACallbackForSingleSide={approveACallbackForSingleSide}
                      showApprovalA={showApprovalA}
                      showApprovalB={showApprovalB}
                      isValid={isValid}
                      parsedAmounts={parsedAmounts}
                      expertMode={expertMode}
                      onAdd={singleSide ? onAddSingleSide : onAdd}
                      setShowConfirm={setShowConfirm}
                      attemptingTxn={attemptingTxn}
                      errorMessage={errorMessage}
                      singleSide={singleSide}
                      setSingleSide={setSingleSide}
                      switchTokenA={switchTokenA}
                      showApprovalAForSingleSide={showApprovalAForSingleSide}
                      presetPriceRange={presetPriceRange}
                      setTriggerInfiniteRange={setTriggerInfiniteRange}
                      handleStep={handleStep}
                      onAddSingleSide={onAddSingleSide}
                      currencyBalances={currencyBalances}
                      errorSingleSide={errorSingleSide}
                      tokenId={tokenId}
                    />
                  </AutoColumn>
                </Wrapper>
              </CustomAppBody>
            </RowContainer>
          </AutoColumn>

          {addIsUnsupported && (
            <UnsupportedCurrencyFooter
              show={addIsUnsupported}
              currencies={[currencies.CURRENCY_A, currencies.CURRENCY_B]}
            />
          )}
        </ScrollablePage>
      </div>
    </>
  )
}
